import { useEffect, useState, useLayoutEffect, useRef } from "react";
import { useLangStore } from "../../store/langStore";
import { t as tr } from "../../lib/i18n";
import { useNbStore } from "../../store/nbStore";

/** Paso: selector data-tutorial + descripción. auto = condición de avance automático. */
interface Paso { sel: string; titulo: string; desc: string; auto?: "signal" | "ied" | "typical"; link?: boolean; }

/** PDF del procedimiento de importación SCL según idioma de la interfaz. */
const BASE = import.meta.env.BASE_URL ?? "/";
const IMPORT_PROC_PDF: Record<string, string> = {
  es: BASE + "docs/publicidad_axon_tags_ES.pdf",
  en: BASE + "docs/procedimiento_importacion_EN.pdf",
};

const PASOS: Paso[] = [
  { sel: "add-file",     titulo: "+ Añadir",
    desc: "Carga un Excel/CSV con más nombres de señal y los agrega al listado actual." },
  { sel: "add-signal",   titulo: "+ Añadir señal", auto: "signal",
    desc: "Haz clic, escribe \"Posición interruptor\" y pulsa Enter. El aplicativo inferirá su nodo MMS y avanzaremos al siguiente paso." },
  { sel: "ied-list",     titulo: "Lista de IEDs", auto: "ied",
    desc: "Selecciona una marca y una referencia de IED. Al elegirla, la señal se re-infiere con ese modelo y avanzamos." },
  { sel: "typicals",     titulo: "Catálogo de típicos", auto: "typical",
    desc: "Abre esta pestaña e inserta un típico de bahía. Al añadirse las señales, avanzamos." },
  { sel: "conf-badge",   titulo: "Ediciones de señal",
    desc: "El símbolo de porcentaje muestra la confianza. Al darle clic te permite seleccionar entre otras posibilidades de nodo lógico IEC 61850." },
  { sel: "generic-dots", titulo: "Nodos genéricos",
    desc: "El símbolo de los tres punticos asocia la señal a un nodo genérico. Con CTRL + clic seleccionas varias filas para asociarlas de manera masiva; con Esc des-seleccionas." },
  { sel: "new-tab",      titulo: "Nuevas pestañas",
    desc: "Con el botón \"+\" creas nuevas pestañas para construir el listado de señales de otras bahías o IEDs." },
  { sel: "download",     titulo: "Descargar",
    desc: "Exporta el listado en formato Axon o en tu propio formato XLSX." },
  { sel: "validate",     titulo: "Validar nodos en SCL",
    desc: "Carga un SCL y comprueba si cada nodo asignado existe realmente en el archivo." },
  { sel: "load-scl",     titulo: "Cargar SCL",
    desc: "Carga el archivo SCL (.scd/.cid) destino donde se mapearán los nodos." },
  { sel: "map-nodes",    titulo: "Mapear nodos a SCL",
    desc: "Escribe los nodos en los DataSets MMS del SCL. Podrás customizar las propiedades Trigger options, Optional fields y Buffered/Unbuffered de los reportes a insertar.", link: true },
];

interface Rect { top: number; left: number; width: number; height: number; }

export function Tutorial({ tabId, onClose }: { tabId: string; onClose: () => void }) {
  const { lang } = useLangStore();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const paso = PASOS[i];

  // Estado observado para el auto-avance
  const nRows  = useNbStore(s => s.getTab(tabId).rows.length);
  const selIed = useNbStore(s => s.getTab(tabId).selectedIed);
  const base   = useRef({ rows: 0, ied: "" });
  useEffect(() => { base.current = { rows: nRows, ied: selIed }; // línea base al entrar al paso
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);
  useEffect(() => {
    if (!paso.auto) return;
    if (paso.auto === "signal"  && nRows  >  base.current.rows) setI(v => Math.min(v + 1, PASOS.length - 1));
    if (paso.auto === "ied"     && selIed && selIed !== base.current.ied) setI(v => Math.min(v + 1, PASOS.length - 1));
    if (paso.auto === "typical" && nRows  >  base.current.rows) setI(v => Math.min(v + 1, PASOS.length - 1));
  }, [nRows, selIed, paso.auto]);

  // Medir el elemento señalado
  useLayoutEffect(() => {
    const medir = () => {
      const el = document.querySelector(`[data-tutorial="${paso.sel}"]`);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    medir();
    const iv = setInterval(medir, 400);   // re-medir (la página sigue interactiva)
    window.addEventListener("resize", medir);
    window.addEventListener("scroll", medir, true);
    return () => { clearInterval(iv);
      window.removeEventListener("resize", medir);
      window.removeEventListener("scroll", medir, true); };
  }, [paso.sel]);

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setI(v => Math.min(v + 1, PASOS.length - 1));
      if (e.key === "ArrowLeft")  setI(v => Math.max(v - 1, 0));
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  const ultimo = i === PASOS.length - 1;
  const cardW = 310;

  // ── Arrastre de la tarjeta con click izquierdo sostenido ──
  const [drag, setDrag] = useState<{ top: number; left: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; top: number; left: number } | null>(null);
  useEffect(() => { setDrag(null); }, [i]);   // al cambiar de paso, vuelve a la posición automática
  const onDragStart = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const tgt = e.target as HTMLElement;
    if (tgt.closest("button, a, select, input")) return;  // no interferir con controles
    const card = e.currentTarget.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, top: card.top, left: card.left };
    const move = (ev: PointerEvent) => {
      const d = dragRef.current; if (!d) return;
      setDrag({
        top:  Math.min(Math.max(0, d.top  + ev.clientY - d.startY), window.innerHeight - 60),
        left: Math.min(Math.max(0, d.left + ev.clientX - d.startX), window.innerWidth  - cardW),
      });
    };
    const up = () => { dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const debajo = rect ? rect.top + rect.height + 170 < window.innerHeight : true;
  const cardTop  = drag ? drag.top  : (rect ? (debajo ? rect.top + rect.height + 14 : Math.max(8, rect.top - 170)) : 90);
  const cardLeft = drag ? drag.left : (rect
    ? Math.min(Math.max(8, rect.left + rect.width / 2 - cardW / 2), window.innerWidth - cardW - 8)
    : window.innerWidth / 2 - cardW / 2);

  return (
    <div className="pointer-events-none fixed inset-0 z-[60]">
      {/* Resaltado (la página permanece interactiva para los pasos guiados) */}
      {rect && (
        <div className="absolute rounded ring-2 ring-[var(--color-brand)]"
             style={{ top: rect.top - 4, left: rect.left - 4,
                      width: rect.width + 8, height: rect.height + 8 }} />
      )}
      {/* Flecha temporal */}
      {rect && (
        <div className="absolute animate-bounce text-2xl text-[var(--color-brand)]"
             style={{ top: debajo ? rect.top + rect.height + 1 : rect.top - 30,
                      left: rect.left + rect.width / 2 - 8 }}>
          {debajo ? "▲" : "▼"}
        </div>
      )}
      {/* Tarjeta */}
      <div onPointerDown={onDragStart}
           className="pointer-events-auto absolute cursor-move select-none rounded-lg border border-[var(--color-brand-border-60)] bg-[var(--color-surface-container)] p-3 shadow-xl"
           style={{ top: cardTop, left: cardLeft, width: cardW, touchAction: "none" }}>
        <div className="mb-1 flex items-center justify-between">
          <span className="font-semibold text-[var(--color-brand)]">{tr(paso.titulo, lang)}</span>
          <span className="text-[10px] text-[var(--color-on-surface-variant)]">{i + 1}/{PASOS.length}</span>
        </div>
        <p className="text-xs leading-relaxed text-[var(--color-on-surface-variant)]">{tr(paso.desc, lang)}</p>
        {paso.link && (
          <a href={IMPORT_PROC_PDF[lang] ?? IMPORT_PROC_PDF.es}
             target="_blank" rel="noopener noreferrer"
             className="mt-1 inline-block text-[11px] font-medium text-[var(--color-brand)] underline">
            {tr("Ver en línea el procedimiento de implantación del SCL resultante en el IED", lang)}
          </a>
        )}
        {paso.auto && (
          <p className="mt-1 text-[10px] italic text-[var(--color-brand)]">
            {tr("Este paso avanza automáticamente al completar la acción.", lang)}
          </p>
        )}
        {!rect && (
          <p className="mt-1 text-[10px] italic text-[var(--color-on-surface-variant)] opacity-60">
            {tr("Este botón no está visible ahora mismo.", lang)}
          </p>
        )}
        <div className="mt-3 flex items-center justify-between gap-2">
          <button onClick={onClose}
            className="text-[11px] text-[var(--color-on-surface-variant)] hover:underline">
            {tr("Cerrar", lang)}
          </button>
          <div className="flex gap-1">
            <button onClick={() => setI(v => Math.max(v - 1, 0))} disabled={i === 0}
              className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[11px] disabled:opacity-30">
              {tr("Anterior", lang)}
            </button>
            <button onClick={() => (ultimo ? onClose() : setI(v => v + 1))}
              className="rounded bg-[var(--color-brand)] px-2 py-0.5 text-[11px] font-medium text-white">
              {ultimo ? tr("Terminar", lang) : tr("Siguiente", lang)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
